// Image Studio — core data model types

export type BlendMode =
  | 'normal'
  | 'multiply'
  | 'screen'
  | 'overlay'
  | 'darken'
  | 'lighten'
  | 'soft-light'
  | 'hard-light'
  | 'color-dodge'
  | 'color-burn'
  | 'difference'
  | 'exclusion';

export type LayerType = 'image' | 'generated' | 'text' | 'shape' | 'adjustment' | 'mask';

export type AdjustmentFilters = {
  brightness?: number;   // 0.0 – 2.0, default 1.0
  contrast?: number;     // 0.0 – 2.0, default 1.0
  saturation?: number;   // 0.0 – 2.0, default 1.0
  hueRotate?: number;    // degrees 0 – 360
  warmth?: number;       // -100 to 100 (maps to sepia + hue)
  blur?: number;         // px, default 0
  vignette?: number;     // 0 – 1
};

export type TextLayerProps = {
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  color: string;
  align: 'left' | 'center' | 'right';
  lineHeight: number;
};

export type ShapeLayerProps = {
  shapeType: 'rect' | 'ellipse' | 'line';
  fill: string;
  stroke: string;
  strokeWidth: number;
  borderRadius?: number;  // rect only
};

export type LayerTransform = {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;  // degrees
};

export type Layer = {
  id: string;
  type: LayerType;
  name: string;
  visible: boolean;
  locked: boolean;
  opacity: number;           // 0 – 1
  blendMode: BlendMode;
  transform: LayerTransform;
  // image/generated layers store pixel data as a data URL or asset ref
  imageData?: string;        // data URL (base64) — transient, pre-persist
  sourceAssetId?: string;    // if persisted to storage
  maskLayerId?: string;
  aiOperationId?: string;
  filters?: AdjustmentFilters;
  textContent?: TextLayerProps;
  shapeContent?: ShapeLayerProps;
};

// Reference images for generation — text + reference + style reference
export type ReferenceImage = {
  id: string;
  role: 'style' | 'structure' | 'composition' | 'subject';
  strength: number;           // 0 – 1
  sourceLayerId?: string;     // use an existing layer as reference
  assetId?: string;           // use a separately imported asset
  dataUrl?: string;           // inline for transient references (pasted images)
};

export type VariantResult = {
  id: string;
  imageDataUrl: string;       // base64 for display
  assetId?: string;           // if persisted to storage
  thumbnailUrl?: string;
  hasTransparency?: boolean;
};

export type AIOperationMode =
  | 'generate'
  | 'edit'
  | 'remove'
  | 'expand'
  | 'restyle'
  | 'upscale'
  | 'bg-replace';

export type AIOperationStatus = 'pending' | 'running' | 'complete' | 'failed' | 'cancelled';

export type ApplyMode = 'replace-layer' | 'new-layer' | 'branch';

export type AIOperation = {
  id: string;
  mode: AIOperationMode;
  status: AIOperationStatus;
  prompt: string;
  negativePrompt?: string;
  sourceLayerIds: string[];
  selectionMaskData?: string;      // base64 mask (white = edit region)
  transparentBackground?: boolean; // request transparent output
  referenceImages?: ReferenceImage[];
  params: {
    strength?: number;
    qualityMode?: 'draft' | 'standard' | 'high';
    seed?: number;
    applyMode?: ApplyMode;
    variantCount?: number;
  };
  // AbortController is NOT stored in Zustand (not serializable).
  // It lives in the aiOperationService keyed by opId.
  resultVariants: VariantResult[];
  acceptedVariantIndex?: number;
  createdAt: string;
  durationMs?: number;
  error?: string;
};

export type HistoryEntryType =
  | 'layer-add'
  | 'layer-delete'
  | 'layer-transform'
  | 'layer-style'
  | 'ai-operation'
  | 'selection-change'
  | 'canvas-resize'
  | 'adjustment'
  | 'layer-replace'
  | 'import';

export type HistoryEntry = {
  id: string;
  type: HistoryEntryType;
  timestamp: number;
  description: string;
  branchId: string;   // 'main' or a branch uuid
  // Undo/redo closures — stored as opaque functions, not serialized
  undo?: () => void;
  redo?: () => void;
};

export type SelectionMode = 'replace' | 'add' | 'subtract' | 'intersect';

export type SelectionType = 'none' | 'rect' | 'lasso' | 'brush' | 'auto-subject' | 'all';

export type Selection = {
  type: SelectionType;
  mode: SelectionMode;     // active painting mode
  // maskCanvas lives OUTSIDE Zustand (not serializable).
  // It is stored in the SelectionEngine and read by the compositor.
  // We store a serial number that increments on every mask update
  // so React can re-render when the mask changes.
  maskSerial: number;
  bounds: { x: number; y: number; width: number; height: number } | null;
  feather: number;         // px, default 2
};

export type Guide = {
  id: string;
  axis: 'horizontal' | 'vertical';
  position: number;        // in canvas coordinates
  locked: boolean;
};

export type AssetRef = {
  id: string;
  path: string;            // S3/MinIO key
  dataUrl?: string;        // transient local copy
  width: number;
  height: number;
  name: string;
};

export type StyleRecipe = {
  id: string;
  name: string;
  promptModifier: string;
  params: Partial<AIOperation['params']>;
  thumbnailUrl?: string;
};

export type ImageStudioProject = {
  id: string;
  title: string;
  canvas: {
    width: number;
    height: number;
    background: string | null;  // null = transparent
  };
  layers: Layer[];
  guides: Guide[];
  savedStyles: StyleRecipe[];
  assets: Record<string, AssetRef>;
  createdAt: string;
  updatedAt: string;
};

export type StudioTool =
  | 'select'
  | 'crop'
  | 'text'
  | 'shape-rect'
  | 'shape-ellipse'
  | 'mask-brush'
  | 'lasso'
  | 'eyedropper'
  | 'pan';

export type ViewTransform = {
  offsetX: number;
  offsetY: number;
  scale: number;
};

export type BrushSettings = {
  size: number;
  hardness: number;        // 0 – 1
  color: string;
};

// The full store state shape
export type ImageStudioState = {
  // Project
  project: ImageStudioProject;

  // Editing state
  selectedLayerIds: string[];
  activeTool: StudioTool;
  selection: Selection;
  brushSettings: BrushSettings;
  viewTransform: ViewTransform;

  // Active selection mask as base64 PNG — computed when selection gesture completes.
  // Used by PromptBar and AI operations so they get the correct mask without
  // needing a reference to the SelectionEngine.
  activeMaskData: string | null;

  // AI operation queue
  aiOperations: AIOperation[];
  activeOperationId: string | null;

  // History
  historyStack: HistoryEntry[];
  historyIndex: number;       // current position
  currentBranchId: string;

  // UI state
  showLayerPanel: boolean;
  showInspector: boolean;
  showHistoryPanel: boolean;
  showVariantsStrip: boolean;
  activeVariantsOpId: string | null;

  // URL of the last variant the user explicitly applied (Accept) inside Image Studio.
  // MediaViewerApp reads this when the user exits Studio so the viewer shows the
  // edited image instead of the original. Null when nothing has been applied yet.
  latestExportUrl: string | null;

  // Initialization
  isInitialized: boolean;
  isSaving: boolean;
};

export const DEFAULT_PROJECT = (): ImageStudioProject => ({
  id: '',
  title: 'Untitled',
  canvas: { width: 1024, height: 1024, background: '#ffffff' },
  layers: [],
  guides: [],
  savedStyles: [],
  assets: {},
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

export const DEFAULT_BRUSH_SETTINGS: BrushSettings = {
  size: 40,
  hardness: 0.8,
  color: 'rgba(255, 255, 255, 0.85)',
};

export const DEFAULT_VIEW_TRANSFORM: ViewTransform = {
  offsetX: 0,
  offsetY: 0,
  scale: 1,
};

export const DEFAULT_SELECTION = (): Selection => ({
  type: 'none',
  mode: 'replace',
  maskSerial: 0,
  bounds: null,
  feather: 2,
});
